import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { get_all_notification, get_read_notification } from './api/notificationApi';
import { formatDateWithTime } from '../../../../app/helper-functions/DateFunction';

// Define the type for a notification item
type NotificationItem = {
    id: number;
    notification_title: string;
    notification_type: string;
    notification_description: string | null;
    data: { leave_policy?: string; dates?: string } | null;
    created_at: string;
    updated_at: string;
    is_read: number;
};

function ViewAllNotifications() {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItemsPerPage, setSelectedItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [notificationList, setNotificationList] = useState<NotificationItem[]>([]);

    const itemsPerPageOptions = [10, 20, 30, 40, 50];
    const isPeople = !!(JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}')?.user?.is_people ?? false);
    const queryClient = useQueryClient();

    const {
        data: paginationData,
        isLoading,
        isError,
    } = useQuery(
        ['notifications', currentPage, selectedItemsPerPage, isPeople],
        () => get_all_notification(currentPage, selectedItemsPerPage, isPeople),
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            onSuccess: (data) => {
                if (data?.data?.notifications && Array.isArray(data.data.notifications)) {
                    setNotificationList(data.data.notifications);
                    setTotalItems(data.data.pagination?.total || 0);
                } else {
                    setNotificationList([]);
                    setTotalItems(0);
                }
            },
            onError: () => {
                setNotificationList([]);
                setTotalItems(0);
            },
        }
    );

    const markRead = useMutation(
        (id: number) => get_read_notification(id, isPeople),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['notifications', currentPage, selectedItemsPerPage, isPeople]);
            },
        }
    );

    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [selectedItemsPerPage]);

    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedItemsPerPage(Number(e.target.value));
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage * selectedItemsPerPage < totalItems) {
            setCurrentPage(currentPage + 1);
        }
    };

    const getBadgeClass = (type: string) => {
        switch (type?.toLowerCase()) {
            case 'approved': return 'badge badge-light-success';
            case 'rejected': return 'badge badge-light-danger';
            case 'leave_approval': return 'badge badge-light-warning';
            default: return 'badge badge-light-secondary';
        }
    };

    const firstItemIndex = totalItems > 0 ? (currentPage - 1) * selectedItemsPerPage + 1 : 0;
    const lastItemIndex = totalItems > 0 ? Math.min(currentPage * selectedItemsPerPage, totalItems) : 0;

    return (
        <div className="card shadow-sm mb-5 mb-xl-10">
            <div className="card-header bg-light border-0">
                <h3 className="card-title fw-bold text-primary m-0">
                    All Notifications
                    {isLoading && !notificationList.length && ' (...)'}
                </h3>
            </div>

            <div className="card-body py-3">
                <div className="table-responsive">
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                        <thead>
                            <tr className="fw-bold text-muted">
                                <th className="min-w-200px">Title</th>
                                <th className="min-w-100px">Type</th>
                                <th className="min-w-300px">Description</th>
                                <th className="min-w-150px">Details</th>
                                <th className="min-w-150px">Created At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && notificationList.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className='text-center'>
                                        <div className='spinner-border' role='status'></div>
                                        <p className='mt-2'>Loading notifications...</p>
                                    </td>
                                </tr>
                            ) : isError ? (
                                <tr>
                                    <td colSpan={5} className='text-center text-danger'>Error loading notifications.</td>
                                </tr>
                            ) : notificationList.length > 0 ? (
                                notificationList.map(notification => {
                                    const parsedDesc = notification.notification_description
                                        ? JSON.parse(notification.notification_description)
                                        : {};

                                    const slots = parsedDesc?.details?.appointment_date_time_slots || [];
                                    const message = parsedDesc?.message || `${parsedDesc?.customer_name} booked with ${parsedDesc?.people_name}`;

                                    return (
                                        <tr
                                            key={notification.id}
                                            className={notification.is_read ? '' : 'bg-light-primary'}
                                            onClick={() => {
                                                if (!notification.is_read && !markRead.isLoading) {
                                                    markRead.mutate(notification.id);
                                                }
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                <div className="text-dark fw-bold fs-6 d-flex align-items-center gap-2">
                                                    {notification.notification_title?.replace(/([a-z])([A-Z])/g, '$1 $2')}
                                                    {!notification.is_read && markRead.variables === notification.id && markRead.isLoading && (
                                                        <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={getBadgeClass(notification.notification_type)}>
                                                    {notification.notification_type?.replace('_', ' ') || 'N/A'}
                                                </span>
                                            </td>
                                            <td><p className="text-dark mb-0">{message}</p></td>
                                            <td>
                                                {slots.length ? (
                                                    <ul className="mb-0 ps-3">
                                                        {slots.map((slot: any, i: number) => (
                                                            <li key={i}>{formatDateWithTime(slot.datetime)}</li>
                                                        ))}
                                                    </ul>
                                                ) : 'N/A'}
                                            </td>
                                            <td><div className="text-dark">{formatDateWithTime(notification.created_at)}</div></td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className='text-center text-muted'>No notifications found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card-footer d-flex flex-wrap justify-content-between align-items-center py-4 border-top bg-light-subtle">
                <div className="d-flex flex-wrap align-items-center">
                    <span className="me-2 text-muted fw-semibold">Results per page:</span>
                    <select
                        className="form-select form-select-sm w-auto me-3"
                        value={selectedItemsPerPage}
                        onChange={handleItemsPerPageChange}
                        disabled={isLoading}
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    {(totalItems > 0 || isLoading) && (
                        <span className="text-muted">
                            {isLoading && !notificationList.length
                                ? 'Loading...'
                                : `${firstItemIndex} to ${lastItemIndex} of ${totalItems}`}
                        </span>
                    )}
                </div>

                <div className="d-flex align-items-center mt-3 mt-sm-0">
                    <span className="me-3 text-muted fw-semibold">
                        Page {currentPage} of {Math.ceil(totalItems / selectedItemsPerPage) || 1}
                    </span>
                    <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || isLoading || totalItems === 0}
                    >
                        ← Previous
                    </button>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleNextPage}
                        disabled={currentPage >= Math.ceil(totalItems / selectedItemsPerPage) || isLoading || totalItems === 0}
                    >
                        Next →
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ViewAllNotifications;
